import { useState } from 'react';
import { Link } from 'react-router-dom';

import {
  useAuth,
  useReconcile,
  useConsolidationJob,
  useDeleteOrg,
  useDeleteRepository,
  useOrgDetail,
  useRepositories,
  useRenameOrg,
  useSetOrgBusinessContext,
  useSetOrgIcon,
  type OrgSummary,
  type Repository,
} from '../api.ts';
import { LinkableRepos } from '../components/LinkableRepos.tsx';
import { OrgIcon } from '../components/OrgIcon.tsx';
import { OrgIconEditor } from '../components/OrgIconEditor.tsx';
import { OnboardingBanner } from '../snapshot/components/OnboardingBanner.tsx';

import styles from './DataSourcesManageOrgs.module.css';

/**
 * /data-sources/manage-orgs — organize repos across orgs.
 *
 * Lists every org the user belongs to, each with its linked repos.
 * Owners can rename or delete an org, remove a repo from it, and add new
 * repos (from the GitHub App's available list) into it. Member-role orgs
 * render read-only — the mutations are owner-gated server-side, so the
 * actions are disabled to match.
 *
 * "Remove" hard-deletes the repo and its ingested history from that org
 * (confirm-gated); the repo stays available to re-add elsewhere via the
 * install list. "Delete" tears down the whole org and everything under
 * it. Both reuse the native confirm dialog already used for the other
 * destructive levers (force reset, re-run onboarding).
 */
export function DataSourcesManageOrgs() {
  const auth = useAuth();
  const orgs = auth.status === 'signed-in' ? auth.me.orgs : [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
      <OnboardingBanner />
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div>
          <h1 className="settings-h1">Manage Orgs</h1>
          <p className="settings-subtitle">Configure org settings and organize repos.</p>
        </div>
        <Link
          to="/onboarding/connect-repo"
          className="snap-toolbar__btn"
          style={{ flexShrink: 0, textDecoration: 'none' }}
        >
          Link New Repo
        </Link>
      </div>

      {orgs.length === 0 ? (
        <p className={styles.muted}>
          No orgs yet. <Link to="/onboarding/connect-repo">Link a repo</Link> to create one.
        </p>
      ) : (
        orgs.map((org) => <OrgSection key={org.slug} org={org} canDelete={orgs.length > 1} />)
      )}
    </div>
  );
}

function OrgSection({ org, canDelete }: { org: OrgSummary; canDelete: boolean }) {
  const isOwner = org.role === 'admin';
  const repositories = useRepositories(org.slug);
  const renameOrg = useRenameOrg();
  const deleteOrg = useDeleteOrg();
  const setOrgIcon = useSetOrgIcon();
  const reconcile = useReconcile(org.slug);
  const [reconcileJobId, setReconcileJobId] = useState<string | null>(null);
  const reconcileJob = useConsolidationJob(org.slug, reconcileJobId);
  const reconcileRunning =
    reconcile.isPending ||
    reconcileJob.data?.status === 'queued' ||
    reconcileJob.data?.status === 'running';

  const [editing, setEditing] = useState(false);
  const [draftName, setDraftName] = useState(org.name);
  const [showAdd, setShowAdd] = useState(false);
  const [editingIcon, setEditingIcon] = useState(false);
  const [editingContext, setEditingContext] = useState(false);

  // Fall back to a linked repo's owner avatar when no custom icon is set.
  const repoAvatar = repositories.data?.find((r) => r.avatar_url)?.avatar_url ?? null;
  const displayedIcon = org.icon ?? repoAvatar;

  const submitRename = (e: React.FormEvent) => {
    e.preventDefault();
    const name = draftName.trim();
    if (!name || name === org.name) {
      setEditing(false);
      return;
    }
    renameOrg.mutate({ slug: org.slug, name }, { onSuccess: () => setEditing(false) });
  };

  const onDeleteOrg = () => {
    const ok = confirm(
      `Delete the org “${org.name}” and everything under it — all linked repos, their ingested history, and the platform map? This cannot be undone.`,
    );
    if (!ok) return;
    deleteOrg.mutate(org.slug);
  };

  const onReconcile = () => {
    const ok = confirm(
      `Reconcile “${org.name}”: merge contexts that consistently change together within each repo into single contexts, and refresh the platform map. This edits the catalog (reversible). Continue?`,
    );
    if (!ok) return;
    reconcile.mutate(undefined, { onSuccess: (d) => setReconcileJobId(d.job_id) });
  };

  return (
    <div className={styles.org}>
      <div className={styles.orgHead}>
        <div className={styles.identity}>
          {isOwner ? (
            <button
              type="button"
              className={styles.iconButton}
              onClick={() => setEditingIcon(true)}
              aria-label={`Edit icon for ${org.name}`}
              title="Edit org icon"
            >
              <OrgIcon name={org.name} icon={displayedIcon} size={32} />
              <span className={styles.editOverlay}>
                <PencilIcon />
              </span>
            </button>
          ) : (
            <OrgIcon name={org.name} icon={displayedIcon} size={32} />
          )}

          {editing ? (
            <form className={styles.renameForm} onSubmit={submitRename}>
              <input
                className={styles.renameInput}
                value={draftName}
                onChange={(e) => setDraftName(e.target.value)}
                autoFocus
                aria-label="Org name"
              />
              <button type="submit" className={styles.action} disabled={renameOrg.isPending}>
                {renameOrg.isPending ? 'Saving…' : 'Save'}
              </button>
              <button
                type="button"
                className={styles.action}
                onClick={() => {
                  setDraftName(org.name);
                  setEditing(false);
                }}
              >
                Cancel
              </button>
            </form>
          ) : (
            <span className={styles.orgName}>{org.name}</span>
          )}
        </div>

        {!editing ? (
          <div className={styles.actions}>
            <button
              type="button"
              className={styles.action}
              disabled={!isOwner}
              title={isOwner ? undefined : 'Only owners can rename an org'}
              onClick={() => {
                setDraftName(org.name);
                setEditing(true);
              }}
            >
              Rename
            </button>
            <button
              type="button"
              className={styles.action}
              title="Prose about this business — steers what the historian investigates"
              onClick={() => setEditingContext((v) => !v)}
              aria-expanded={editingContext}
            >
              Business context
            </button>
            <button
              type="button"
              className={styles.action}
              disabled={!isOwner || reconcileRunning}
              title={
                isOwner
                  ? 'Merge co-changing contexts within each repo + refresh the map'
                  : 'Only owners can reconcile'
              }
              onClick={onReconcile}
            >
              {reconcileRunning ? 'Reconciling…' : 'Reconcile'}
            </button>
            <button
              type="button"
              className={`${styles.action} ${styles.danger}`}
              disabled={!isOwner || !canDelete || deleteOrg.isPending}
              title={
                !isOwner
                  ? 'Only owners can delete an org'
                  : !canDelete
                    ? 'You can’t delete your only org'
                    : undefined
              }
              onClick={onDeleteOrg}
            >
              {deleteOrg.isPending ? 'Deleting…' : 'Delete'}
            </button>
          </div>
        ) : null}
      </div>

      {renameOrg.isError ? <p className={styles.error}>{renameOrg.error.message}</p> : null}
      {deleteOrg.isError ? <p className={styles.error}>{deleteOrg.error.message}</p> : null}
      {reconcile.isError ? <p className={styles.error}>{reconcile.error.message}</p> : null}
      {reconcileJob.data?.status === 'failed' ? (
        <p className={styles.error}>
          Reconcile failed{reconcileJob.data.error_message ? `: ${reconcileJob.data.error_message}` : ''}.
        </p>
      ) : null}

      {reconcileRunning && reconcileJobId ? (
        <p className={`${styles.muted} ${styles.repoRow}`}>
          Reconciling… scanning co-change across the catalog (this can take a minute on a large org).
        </p>
      ) : null}

      {reconcileJob.data?.result ? (
        <div className={styles.reconcile}>
          {reconcileJob.data.result.merges.length === 0 ? (
            <p className={styles.muted}>
              Nothing to reconcile — derivedContexts are already well-separated (at{' '}
              {reconcileJob.data.result.threshold}% co-change).
            </p>
          ) : (
            <>
              <p className={styles.reconcileHead}>
                Merged {reconcileJob.data.result.merges.length}{' '}
                {reconcileJob.data.result.merges.length === 1 ? 'group' : 'groups'} (
                {reconcileJob.data.result.derived_contexts_deprecated.length} derivedContexts absorbed,{' '}
                {reconcileJob.data.result.clusters_updated} clusters re-pointed) at{' '}
                {reconcileJob.data.result.threshold}% co-change:
              </p>
              <ul className={styles.reconcileList}>
                {reconcileJob.data.result.merges.map((m) => (
                  <li key={m.survivor}>
                    <span className={styles.muted}>({m.repo})</span> {m.display ?? m.survivor}{' '}
                    <span className={styles.muted}>← {m.absorbed.join(', ')}</span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      ) : null}

      {editingIcon ? (
        <OrgIconEditor
          orgName={org.name}
          currentIcon={org.icon}
          saving={setOrgIcon.isPending}
          error={setOrgIcon.error?.message ?? null}
          onSave={(icon) =>
            setOrgIcon.mutate(
              { slug: org.slug, icon },
              { onSuccess: () => setEditingIcon(false) },
            )
          }
          onClose={() => setEditingIcon(false)}
        />
      ) : null}

      {repositories.isPending ? (
        <p className={`${styles.muted} ${styles.repoRow}`}>Loading repos…</p>
      ) : repositories.isError ? (
        <p className={`${styles.error} ${styles.repoRow}`}>{repositories.error.message}</p>
      ) : repositories.data.length === 0 ? (
        <p className={`${styles.muted} ${styles.repoRow}`}>No repos linked to this org yet.</p>
      ) : (
        repositories.data.map((repo) => (
          <RepoRow key={repo.id} orgSlug={org.slug} repo={repo} canRemove={isOwner} />
        ))
      )}

      <div className={styles.addRow}>
        <button
          type="button"
          className={`${styles.action} ${styles.add}`}
          disabled={!isOwner}
          title={isOwner ? undefined : 'Only owners can add repos'}
          onClick={() => setShowAdd((v) => !v)}
          aria-expanded={showAdd}
        >
          + Add New Repo
        </button>
      </div>

      {showAdd ? (
        <div className={styles.available}>
          {/* Available-via-GitHub-App repos, linked into THIS org. */}
          <LinkableRepos orgSlug={org.slug} defaultOpen />
        </div>
      ) : null}

      {editingContext ? <BusinessContextEditor orgSlug={org.slug} canEdit={isOwner} /> : null}
    </div>
  );
}

/** Matches `BUSINESS_CONTEXT_MAX` on the server (routes/orgs.ts). */
const BUSINESS_CONTEXT_MAX = 8_000;

/**
 * The org's business context — prose about what the business is and what it is
 * trying to do, which the historian reads as a set of QUESTIONS to investigate
 * against the commit record. It is never treated as fact, and the copy here says
 * so: someone typing a confident claim should know it will be checked, not
 * repeated.
 *
 * Loaded on demand (its own `useOrgDetail` query) rather than from `/me`, which
 * carries every org and has no business hauling kilobytes of prose per page load.
 */
function BusinessContextEditor({ orgSlug, canEdit }: { orgSlug: string; canEdit: boolean }) {
  const detail = useOrgDetail(orgSlug);
  const save = useSetOrgBusinessContext();
  const [draft, setDraft] = useState<string | null>(null);

  const saved = detail.data?.business_context ?? '';
  // `null` means "not edited yet" — so the textarea picks up the fetched value
  // once it lands without clobbering anything already typed.
  const value = draft ?? saved;
  const dirty = draft !== null && draft.trim() !== saved.trim();
  const over = value.length > BUSINESS_CONTEXT_MAX;

  if (detail.isPending) return <p className={`${styles.muted} ${styles.repoRow}`}>Loading…</p>;
  if (detail.isError) return <p className={`${styles.error} ${styles.repoRow}`}>{detail.error.message}</p>;

  return (
    <div className={styles.contextBlock}>
      <p className={styles.muted} style={{ margin: 0 }}>
        What this business is and what it is trying to do — in plain prose. It steers where the
        historian looks; it is never taken as fact. Every claim gets checked against the commit
        record, and “no evidence found” is a result we report.
      </p>
      <textarea
        className={styles.contextArea}
        value={value}
        disabled={!canEdit || save.isPending}
        onChange={(e) => setDraft(e.target.value)}
        aria-label="Business context"
        placeholder={
          'e.g. A legacy B2B VoIP provider. Growth has been flat for years. With AI the team has ' +
          'invested heavily in AI readiness, wants to mine its data-rich customer base for new ' +
          'products, and to cut maintenance cost. The CTO joined in 2024.'
        }
      />
      <div className={styles.contextFoot}>
        <button
          type="button"
          className={styles.action}
          disabled={!canEdit || !dirty || over || save.isPending}
          title={canEdit ? undefined : 'Only owners can edit the business context'}
          onClick={() =>
            save.mutate(
              { slug: orgSlug, businessContext: value.trim() ? value.trim() : null },
              { onSuccess: () => setDraft(null) },
            )
          }
        >
          {save.isPending ? 'Saving…' : 'Save'}
        </button>
        {dirty ? (
          <button type="button" className={styles.action} onClick={() => setDraft(null)}>
            Revert
          </button>
        ) : null}
        <span className={`${styles.contextCount} ${over ? styles.over : ''}`}>
          {value.length.toLocaleString()} / {BUSINESS_CONTEXT_MAX.toLocaleString()}
        </span>
      </div>
      {save.isError ? <p className={styles.error}>{save.error.message}</p> : null}
    </div>
  );
}

function RepoRow({
  orgSlug,
  repo,
  canRemove,
}: {
  orgSlug: string;
  repo: Repository;
  canRemove: boolean;
}) {
  const deleteRepo = useDeleteRepository();

  const onRemove = () => {
    const ok = confirm(
      `Remove ${repo.prefix} from this org? This permanently deletes its ingested history (commits, file paths, ratings). The repo stays available to re-add to another org. This cannot be undone.`,
    );
    if (!ok) return;
    deleteRepo.mutate({ orgSlug, repoId: repo.id });
  };

  return (
    <div className={styles.repoRow}>
      <span className={styles.repoName}>{repo.prefix}</span>
      <div className={styles.actions}>
        <button
          type="button"
          className={`${styles.action} ${styles.danger}`}
          disabled={!canRemove || deleteRepo.isPending}
          title={canRemove ? undefined : 'Only owners can remove repos'}
          onClick={onRemove}
        >
          {deleteRepo.isPending ? 'Removing…' : 'Remove'}
        </button>
      </div>
    </div>
  );
}

function PencilIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4Z" />
    </svg>
  );
}
